<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import AppSidebar from '@/components/AppSidebar.vue';
import AudioPlayer from '@/components/AudioPlayer.vue';
import MainHeader from '@/layouts/frame/MainHeader.vue';
import CommandPalette from '@/components/commandPalette/CommandPalette.vue';
import InspectorPanel from '@/components/InspectorPanel.vue';
import InspectorSheet from '@/components/InspectorSheet.vue';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import MobileAreaBar from '@/layouts/frame/MobileAreaBar.vue';
import { useAudioPlaybackStore } from '@/stores/playerStore';
import { usePublishedPlayerHeight } from '@/composables/usePublishedPlayerHeight';

const route = useRoute();

/**
 * Every entity list shares one page component. Keying the routed view by entity
 * makes moving from /players to /clients/zones mount a fresh page, so one list's
 * paging and row selection never carry into the next.
 */
const routedViewKey = computed(() =>
  typeof route.meta.entityId === 'string' ? route.meta.entityId : String(route.name ?? ''),
);
const playbackStore = useAudioPlaybackStore();

/**
 * Mirrors exactly how <AudioPlayer> decides to render its docked bar
 * (`v-if="visible"` driven by the playback store's `visible` ref).
 *
 * This is what makes the published height authoritative on the way DOWN. The
 * bar leaves on a transform, so it is still in flow (and still full height)
 * when it is on its way off screen — measuring at that moment would publish a
 * reservation for a player nobody can see.
 */
const playerVisible = computed(() => playbackStore.visible);

/**
 * Measured so full-viewport overlays can stop above the player rather than
 * running under it. See the composable for the full account.
 */
const playerSlotEl = ref<HTMLElement | null>(null);
usePublishedPlayerHeight(playerSlotEl, playerVisible);

</script>

<template>
  <!-- 56px: the icon strip, which stays when the section column is hidden. -->
  <!-- `data-app-shell` tells app.css to stop the page itself from scrolling. -->
  <SidebarProvider icon-width="56px" :reserved-width-px="56" data-app-shell>
    <AppSidebar collapsible="icon"  />
    <SidebarInset>
      <!-- On a phone the areas live in a floating bar along the bottom. It rides
           above the docked audio player using the height the player publishes,
           which varies with the waveform and wrapping, so a fixed offset would
           put the bar back underneath it. -->
      <MobileAreaBar />
      <!--
        Content region: the routed view plus the side-peek inspector. The
        wrapper is `relative` so InspectorPanel's `absolute inset-y-0` is
        bounded HERE — it stops at the audio player below instead of
        spanning over it.
      -->
      <div class="relative flex min-h-0 min-w-0 flex-1 flex-col">
        <div class="z-0 flex min-h-0 min-w-0 flex-1 flex-col">
          <MainHeader />
          <router-view :key="routedViewKey" />
        </div>
        <InspectorPanel />
        <CommandPalette />
      </div>
      <!--
        Single global audio player. Mounted here — inside SidebarInset,
        below the content region — so there is exactly ONE instance for the
        whole app: it is content-width (shifts with the sidebar collapse/
        expand instead of bleeding under it), it survives route changes and
        inspector open/close, and the player store always has its <audio>
        element attached. It is `visible`-gated, so it only appears while
        something is playing. The Play column (track matching / search) and
        the audio-file-issues inspector all drive this one player.

        It carries no z-index, so the user-menu dropdown and the sidebar
        trigger stay above it.
      -->
      <!--
        The wrapper exists to be MEASURED. Its height is published as
        `--global-audio-player-height` on <html> so full-viewport overlays can
        end above the player instead of running underneath it (see
        `usePublishedPlayerHeight`). It wraps the player rather than the player
        measuring itself because the bar sits inside a <Transition>, and a
        template ref there does not bind back to the AudioPlayer instance.

        It collapses to 0 when nothing is playing, so the variable falls to 0px
        on its own and every overlay goes back to full height with no extra
        bookkeeping.
      -->
      <div ref="playerSlotEl" class="shrink-0">
        <!--
          `pointer-events-auto` is UNCONDITIONAL. Every modal dialog (the
          LZP command picker, anything else reka opens as modal) puts
          `pointer-events: none` on <body>, and the player
          lives outside the dialog's content node so it inherits the lock.
          Without this the bar is visible beside those overlays but its pause
          button does nothing. Declaring it always costs nothing — `auto` is
          already the default when no ancestor has locked events.
        -->
        <AudioPlayer class="pointer-events-auto shrink-0" />
      </div>
      <!-- On a phone every peek is this bottom sheet. -->
      <InspectorSheet />
    </SidebarInset>
  </SidebarProvider>
</template>
